import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Onglets shadcn/ui au système Voxler Router (`tabs` / `tab` / `tab-count`) :
 * piste `muted` rayon lg, onglet 28px en 13px, onglet actif sur `background`
 * avec shadow-sm. `TableTabs` couvre le cas des maquettes où les onglets
 * filtrent un tableau (pas de panneau associé).
 */

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      'inline-flex max-w-full items-center gap-0.5 overflow-x-auto rounded-lg bg-muted p-[3px]',
      className,
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const tabTriggerClass = [
  'group/tab inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-md px-3 text-[13px] font-medium leading-[18px] text-muted-foreground',
  'hover:text-foreground',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
  'disabled:cursor-not-allowed disabled:opacity-50',
  'data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm',
].join(' ');

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger ref={ref} className={cn(tabTriggerClass, className)} {...props} />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

interface TabsContentProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content> {
  /**
   * Garde le panneau monté quand il est masqué (les formulaires conservent leur
   * saisie d'un onglet à l'autre) : `forceMount` + masquage du panneau inactif.
   */
  keepMounted?: boolean;
}

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  TabsContentProps
>(({ className, keepMounted = false, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      keepMounted && 'data-[state=inactive]:hidden',
      className,
    )}
    {...(keepMounted ? { forceMount: true } : {})}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

/** `tab-count` : compteur mono 11px à droite du libellé d'onglet. */
function TabCount({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <span className="font-mono text-[11px] font-medium leading-4 text-muted-foreground group-data-[state=active]/tab:text-foreground">
      {children}
    </span>
  );
}

export interface TableTabItem<T extends string> {
  value: T;
  label: React.ReactNode;
  /** Compteur affiché après le libellé ; omis si `undefined`. */
  count?: number | string | undefined;
  disabled?: boolean;
}

interface TableTabsProps<T extends string> {
  value: T;
  onValueChange: (value: T) => void;
  items: readonly TableTabItem<T>[];
  /** Nom accessible du groupe (« Filtrer par statut »). */
  'aria-label': string;
  className?: string;
}

/**
 * Onglets de filtre d'un tableau (à gauche d'une `CardBar`) : valeur typée,
 * compteur optionnel, navigation clavier Radix. Aucun panneau : le contenu
 * filtré est rendu à côté par la page.
 */
function TableTabs<T extends string>({
  value,
  onValueChange,
  items,
  className,
  ...props
}: TableTabsProps<T>): React.JSX.Element {
  const handleChange = (next: string): void => {
    const item = items.find((candidate) => candidate.value === next);
    if (item !== undefined) {
      onValueChange(item.value);
    }
  };
  return (
    <Tabs value={value} onValueChange={handleChange} activationMode="manual">
      <TabsList aria-label={props['aria-label']} className={className}>
        {items.map((item) => (
          <TabsTrigger key={item.value} value={item.value} disabled={item.disabled === true}>
            {item.label}
            {item.count !== undefined ? <TabCount>{item.count}</TabCount> : null}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent, TabCount, TableTabs, tabTriggerClass };
